import { Module } from "vuex";
import { IRootState } from "@monorepo/reports/src/store";
import { QUERY_PATH } from "@monorepo/utils/src/api/queryPath";
import { encodeQueryData, getFullPath, getQuery } from "@monorepo/utils/src/api/utils";
import { ITableFiltersObject } from "@monorepo/utils/src/store/types/tableFiltersObject";
import { IReportElement } from "@monorepo/reports/src/views/InventoryReportView/types/reportElement";
import { ISection, Sections } from "@monorepo/utils/src/types/cellsSections";
import { actions } from "@monorepo/utils/src/store/modules/actions";
import { getters } from "@monorepo/utils/src/store/modules/getters";
import { mutations } from "@monorepo/utils/src/store/modules/mutations";
import { ISearchTemplate } from "@monorepo/utils/src/types/ISearchTemplate";
import { showNotification } from "@monorepo/utils/src/eventBus/utils/showNotification";
import axios from "axios";
import { convertFiltersToApi, IConvertFiltersToApiResponse } from "@monorepo/utils/src/api/convertFiltersToApi";
import { convertFiltersCustomToApi } from "@monorepo/reports/src/views/InventoryReportView/utils/convertFiltersToApi";
import { IAutocompleteElement } from "@monorepo/utils/src/types/oikAutocompleteType";

interface ITableState {
  filters: Partial<ITableFiltersObject>;
  totalLength: number;
  section?: Sections;
  infiniteId: string | null;
  sectionCells: ISection;
  data: IReportElement[];
  isOpenFilters: boolean;
  searchTemplates: ISearchTemplate[];
  cells: string[];
  libraries: null;
  isTableLoading: boolean;
  isLoadingToggleFilters: boolean;
}

const defaultFilters = (): Partial<ITableFiltersObject> => ({
  fieldFilters: {},
  sort: {},
  initMessagesLength: 50,
});

export const module: Module<ITableState, IRootState> = {
  namespaced: true,
  state: (): ITableState => ({
    filters: defaultFilters(),
    sectionCells: {} as ISection,
    infiniteId: null,
    section: Sections.INVENTORY_REPORT,
    totalLength: 0,
    cells: [],
    searchTemplates: [],
    isOpenFilters: false,
    data: [],
    libraries: null,
    isTableLoading: false,
    isLoadingToggleFilters: false,
  }),
  mutations: {
    ...mutations,
    addData(
      state: ITableState,
      payload: {
        data: IReportElement[];
        totalLength: number;
      }
    ) {
      state.data = state.data.concat(payload.data);
      state.totalLength = payload.totalLength;
    },
    clearEventList(state: ITableState) {
      state.data = [];
      state.totalLength = 0;
    },
    setTableLoading(state: ITableState, payload: boolean) {
      state.isTableLoading = payload;
    },
  },
  actions: {
    ...actions,
    clearEventList({ commit }) {
      commit("clearEventList");
    },
    async getAutocompleteFilterByCode(info, params: string) {
      const { data } = await getQuery<IAutocompleteElement[]>(
        QUERY_PATH.GET_FUND_AUTOCOMPLETE_ITEMS,
        {
          keyword: params,
        },
        false
      );
      if (data !== null) {
        return data.map((item: IAutocompleteElement) => ({
          ...item,
          titleValue: item.isSearchCode ? item.code : item.name,
        }));
      }
      return [];
    },
    async getEventList({ commit, state, rootGetters }) {
      if (rootGetters["auth/isShowAnimation"]) {
        commit("setTableLoading", true);
      }
      const params = convertFiltersToApi(state.filters as ITableFiltersObject, state.data.length, convertFiltersCustomToApi);
      const {
        data,
        total: totalLength,
        error,
      } = await getQuery<IReportElement[]>(QUERY_PATH.GET_ARCHIVE_DOCUMENTS_REPORTS_LIST, params).finally(() => {
        commit("setTableLoading", false);
      });

      if (!data?.length) {
        showNotification("По заданным параметрам данные в системе отсутствуют.");
      }

      if (data !== null) {
        commit("addData", { data: data || [], totalLength: totalLength || data?.length || 0 });
        return { data: state.data || [], totalLength: totalLength || data?.length || 0 };
      }
      return { data: null, error };
    },
    async getExportData({ state, commit }, payload: "PDF" | "DOCX") {
      const params: IConvertFiltersToApiResponse = convertFiltersToApi(
        state.filters as ITableFiltersObject,
        state.data.length,
        convertFiltersCustomToApi,
        true
      );
      params.cells = state.filters.cells || [];
      delete params.sort;
      const query = payload === "PDF" ? QUERY_PATH.GET_ARCHIVE_DOCUMENTS_REPORTS_EXPORT_PDF : QUERY_PATH.GET_ARCHIVE_DOCUMENTS_REPORTS_EXPORT_DOCX;
      commit("app/setIsExportFileLoading", true, {
        root: true,
      });
      const { data } = await getQuery<void>(query, params);
      if (!data) {
        commit("app/setIsExportFileLoading", false, {
          root: true,
        });
      }
    },
    async updateScheduledReport({ dispatch }, payload: ISearchTemplate) {
      try {
        await axios.put(QUERY_PATH.GET_SCHEDULED_REPORT, payload);
        await dispatch("getSearchTemplates");
      } catch (e) {
        console.error(e);
      }
    },
  },
  getters: {
    ...getters,
    isTableLoading(state: ITableState) {
      return state.isTableLoading;
    },
    cells() {
      return [];
    },
  },
};
